import { useState } from "react";
import {
  Star,
  MessageCircle,
  Mail,
  ShieldCheck,
  Factory,
  Truck,
  Store,
} from "lucide-react";
import {
  Button,
  Badge,
  DataTable,
  ProductListConfig,
  ColumnConfig,
} from "@b2b/ui-components";

interface Supplier {
  id: number;
  name: string;
  logo: string;
  country: string;
  countryFlag: string;
  yearsInBusiness: number;
  verified: boolean;
  rating: number;
  reviewCount: number;
  businessType: string;
  mainProducts: string[];
  productImages: string[];
  responseRate: string;
  responseTime: string;
}

interface SupplierTableProps {
  suppliers: Supplier[];
}

export const SupplierTable = ({ suppliers }: SupplierTableProps) => {
  const [imageErrors, setImageErrors] = useState<Record<number, boolean>>({});

  const getCompanyInitials = (name: string) =>
    name
      .split(" ")
      .map((word) => word.charAt(0))
      .join("")
      .toUpperCase()
      .slice(0, 2);

  const handleImageError = (supplierId: number) => {
    setImageErrors((prev) => ({ ...prev, [supplierId]: true }));
  };

  const renderStars = (rating: number) => (
    <div className="flex items-center space-x-1">
      {[...Array(5)].map((_, i) => (
        <Star
          key={i}
          className={`h-3 w-3 ${
            i < Math.floor(rating)
              ? "fill-orange-600 text-orange-500"
              : "text-gray-200"
          }`}
        />
      ))}
      <span className="text-xs text-gray-500 ml-1">({rating})</span>
    </div>
  );

  const getBusinessTypeIcon = (type: string) => {
    switch (type.toLowerCase()) {
      case "manufacturer":
        return <Factory className="h-3 w-3" />;
      case "exporter":
        return <Truck className="h-3 w-3" />;
      case "wholesaler":
        return <Store className="h-3 w-3" />;
      default:
        return <Factory className="h-3 w-3" />;
    }
  };

  const config: ProductListConfig = {
    columns: [
      {
        key: "supplier",
        label: "Supplier",
        width: "w-[300px]",
        render: (row) => {
          const supplier = row as Supplier;
          return (
            <div className="flex gap-3 items-start">
              <div className="w-10 h-10 rounded-lg overflow-hidden flex-shrink-0 bg-gray-100 flex items-center justify-center">
                {!imageErrors[supplier.id] ? (
                  <img
                    src={supplier.logo}
                    alt={`${supplier.name} logo`}
                    className="w-full h-full object-cover"
                    onError={() => handleImageError(supplier.id)}
                  />
                ) : (
                  <div className="w-full h-full bg-orange-100 flex items-center justify-center">
                    <span className="text-orange-500 font-semibold text-sm">
                      {getCompanyInitials(supplier.name)}
                    </span>
                  </div>
                )}
              </div>
              <div className="min-w-0 flex-1">
                <h3 className="font-semibold text-sm text-foreground line-clamp-2 mb-1">
                  {supplier.name}
                </h3>
                <div className="flex items-center gap-2 mb-1">
                  {supplier.verified && (
                    <Badge
                      variant="outline"
                      className="text-xs text-green-700 border-green-500 bg-green-100"
                    >
                      <ShieldCheck className="h-3 w-3 mr-1" />
                      Verified
                    </Badge>
                  )}
                  <span className="text-xs text-gray-500">
                    {supplier.yearsInBusiness} years
                  </span>
                </div>
                <div className="flex items-center gap-2 text-xs text-gray-500">
                  <span>{supplier.countryFlag}</span>
                  <span>{supplier.country}</span>
                </div>
                {/* {renderStars(supplier.rating)} */}
              </div>
            </div>
          );
        },
      },
      {
        key: "products",
        label: "Products",
        render: (row) => {
          const supplier = row as Supplier;
          return (
            <div className="space-y-2">
              <div className="flex gap-1">
                {supplier.productImages.slice(0, 3).map((image, index) => (
                  <div
                    key={index}
                    className="w-8 h-8 rounded overflow-hidden bg-gray-100"
                  >
                    <img
                      src={image}
                      alt={`Product ${index + 1}`}
                      className="w-full h-full object-cover"
                    />
                  </div>
                ))}
                {supplier.productImages.length > 3 && (
                  <div className="w-8 h-8 rounded bg-gray-100 flex items-center justify-center text-xs text-gray-500">
                    +{supplier.productImages.length - 3}
                  </div>
                )}
              </div>
              <p className="text-xs text-gray-500 line-clamp-2">
                {supplier.mainProducts.join(", ")}
              </p>
            </div>
          );
        },
      },
      {
        key: "business",
        label: "Business Info",
        render: (row) => {
          const supplier = row as Supplier;
          return (
            <div className="space-y-1 text-xs">
              <div className="flex items-center gap-1">
                {getBusinessTypeIcon(supplier.businessType)}
                <span className="font-medium text-foreground">
                  {supplier.businessType}
                </span>
              </div>
              <div className="text-gray-500">
                {supplier.reviewCount} reviews
              </div>
            </div>
          );
        },
      },
      {
        key: "response",
        label: "Response",
        render: (row) => {
          const supplier = row as Supplier;
          return (
            <div className="space-y-1 text-xs">
              <div>
                <span className="text-gray-500">Rate: </span>
                <span className="font-medium text-foreground">
                  {supplier.responseRate}
                </span>
              </div>
              <div>
                <span className="text-gray-500">Time: </span>
                <span className="font-medium text-foreground">
                  {supplier.responseTime}
                </span>
              </div>
            </div>
          );
        },
      },
      {
        key: "actions",
        label: "Actions",
        width: "w-[200px]",
        render: () => (
          <div className="space-y-2">
            <Button size="sm" className="w-full text-xs" variant="primary">
              <Mail className="h-3 w-3 mr-1" />
              Contact
            </Button>
            <Button variant="outline" size="sm" className="w-full text-xs">
              <MessageCircle className="h-3 w-3 mr-1" />
              Chat
            </Button>
          </div>
        ),
      },
    ],
    styles: {
      headerBg: "bg-gray-50",
      headerText: "text-gray-700",
      rowHover: "hover:bg-gray-50",
      statusStyles: {},
    },
  };

  return <DataTable data={suppliers} config={config} rowsPerPage={5} />;
};

export default SupplierTable;
